<template>
  <v-container fluid fill-width class="ma-0 pa-2">
    <v-app-bar app class="primary">
      <v-icon color="white" large>home</v-icon>
      <v-toolbar-title class="white--text pl-2"
        ><span>Home Page</span>
      </v-toolbar-title>
    </v-app-bar>

    <!-- Options/actions -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2 font-weight-bold"
                ><span>Welcome to POD-Vis</span>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-container class="ma-2 my-4">
        <v-row>
          <v-col cols="12">
            <div class="text--primary title">
              Please choose one of the following options to get started:
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-btn color="primary" @click="$router.push('/datasets')"
              ><v-icon left large color="white">library_add</v-icon
              ><span class="pl-2">Create new study dataset</span></v-btn
            >
            <span class="pl-2"
              >- select subjects and variables from uploaded dataset(s) to
              create a new study dataset.</span
            >
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-btn color="primary" @click="$router.push('/study_datasets')"
              ><v-icon left large color="white">library_books</v-icon
              ><span class="pl-2">Use saved study dataset</span></v-btn
            >
            <span class="pl-2"
              >- use a previously-created saved study dataset to continue or
              revisit an analysis.</span
            >
          </v-col>
        </v-row>

        <v-row v-if="'data_preview' in features && features['data_preview']">
          <v-col cols="12">
            <v-btn color="primary" @click="$router.push('/data_preview')"
              ><v-icon left large color="white">mdi-chart-timeline</v-icon
              ><span class="pl-2">Data Preview</span></v-btn
            >
            <span class="pl-2"
              >- Preview/visualize the subject visit data in a tab or
              comma-separated flat file.</span
            >
          </v-col>
        </v-row>

        <v-row
          v-if="
            'individual_audiogram' in features &&
              features['individual_audiogram']
          "
        >
          <v-col cols="12">
            <v-btn color="primary" @click="$router.push('/patient_audiogram')"
              ><v-icon left large color="white">mdi-ear-hearing</v-icon
              ><span class="pl-2">Patient audiogram</span></v-btn
            >
            <span class="pl-2"
              >- Display individual patient audiograms alongside user-defined
              study groups.</span
            >
          </v-col>
        </v-row>

        <v-row> <v-col cols="12"> </v-col> </v-row>
      </v-container>
    </v-sheet>

    <!-- Groups -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pa-0 pb-2">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2 font-weight-bold"
                >Groups
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <div v-if="sortedGroups.length == 0" class="pa-2 pb-4 title">
        <span> No groups. </span>
      </div>

      <v-container fluid fill-width class="mt-2 mb-3 px-4">
        <v-row class="mb-2">
          <!-- first column -->
          <v-col cols="6" class="ma-0 pa-0 pl-3">
            <ul>
              <li
                v-for="(ug, ugi) in sortedGroups.slice(
                  0,
                  Math.ceil(sortedGroups.length / 2)
                )"
                :key="'ug-' + ugi"
              >
                <v-tooltip
                  v-if="ug.is_leader || ug.is_contributor"
                  top
                  color="primary"
                >
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      <v-chip label class="primary my-1">{{ ug.name }}</v-chip>
                    </span>
                  </template>
                  <span class="subtitle-1">
                    {{ ug.name }} users:<br clear="both" />
                    <ul>
                      <li
                        v-for="(u, ui) in ug.users"
                        :key="'uli-' + ui"
                        class="ma-0 pa-0"
                      >
                        {{ u.name }} / {{ u.institution
                        }}<v-icon v-if="u.is_leader" color="white" class="pl-1"
                          >mdi-human-male-board</v-icon
                        ><v-icon
                          v-if="u.is_contributor"
                          color="white"
                          class="pl-1"
                          >mdi-share-variant</v-icon
                        ><br clear="both" />
                      </li>
                    </ul>
                  </span>
                </v-tooltip>

                <span v-else> {{ ug.name }} </span>
                <v-icon v-if="ug.is_leader" class="pl-1"
                  >mdi-human-male-board</v-icon
                ><v-icon v-if="ug.is_contributor" class="pl-1"
                  >mdi-share-variant</v-icon
                ><br clear="both" />
              </li>
            </ul>
          </v-col>

          <!-- 2nd column -->
          <v-col cols="6" class="ma-0 pa-0 pl-3">
            <ul>
              <li
                v-for="(ug, ugi) in sortedGroups.slice(
                  Math.ceil(sortedGroups.length / 2)
                )"
                :key="'ug-' + ugi"
              >
                <v-tooltip
                  v-if="ug.is_leader || ug.is_contributor"
                  top
                  color="primary"
                >
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">
                      <v-chip label class="primary my-1">{{ ug.name }}</v-chip>
                    </span>
                  </template>
                  <span class="subtitle-1">
                    {{ ug.name }} users:<br clear="both" />
                    <ul>
                      <li
                        v-for="(u, ui) in ug.users"
                        :key="'uli-' + ui"
                        class="ma-0 pa-0"
                      >
                        {{ u.name }} / {{ u.institution
                        }}<v-icon v-if="u.is_leader" color="white" class="pl-1"
                          >mdi-human-male-board</v-icon
                        ><v-icon
                          v-if="u.is_contributor"
                          color="white"
                          class="pl-1"
                          >mdi-share-variant</v-icon
                        ><br clear="both" />
                      </li>
                    </ul>
                  </span>
                </v-tooltip>

                <span v-else> {{ ug.name }} </span>
                <v-icon v-if="ug.is_leader" class="pl-1"
                  >mdi-human-male-board</v-icon
                ><v-icon v-if="ug.is_contributor" class="pl-1"
                  >mdi-share-variant</v-icon
                ><br clear="both" />
              </li>
            </ul>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="left">
            <span class="font-weight-bold">Group Roles:</span>
            <v-icon class="pl-2">mdi-human-male-board</v-icon> Leader
            <v-icon class="pl-2">mdi-share-variant</v-icon> Contributor
          </v-col>
        </v-row>
      </v-container>
    </v-sheet>

    <!-- Activity Feed -->
    <v-sheet color="white" class="scroll rounded-lg shadow my-2 mt-4">
      <v-container fluid fill-width class="ma-0 pa-0">
        <v-row class="ma-0 pb-2">
          <v-col cols="12" class="ma-0 pa-0">
            <v-card color="#eeeeee" class="pt-1">
              <v-card-title class="primary--text pl-3 py-2 font-weight-bold"
                >Activity Feed
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <div class="pa-2 pb-4 title">
        <span v-if="feedLoading"> Loading... </span>
        <span v-else-if="feed.length == 0"> No activity. </span>
        <span v-else>
          <v-pagination
            v-model="pageNumber"
            :length="Math.ceil(feed.length / postsPerPage)"
          >
          </v-pagination>
        </span>
      </div>

      <div class="feed-messages pl-0 mt-2 mb-3">
        <div
          v-for="(f, fi) in getCurrentFeedPage()"
          :key="'ufsl-' + fi"
          class="message-row"
        >
          <div v-if="isNewDay(f, fi)" :key="'day-' + f.id" class="day-banner">
            <span class="day-banner-label">{{
              formatDayLabel(f.date_generated)
            }}</span>
          </div>
          <div class="msg-avatar">{{ userInitial(f.user) }}</div>
          <div class="msg-body">
            <div class="msg-meta d-flex align-center flex-wrap">
              <span class="font-weight-bold" style="font-size: 0.85em;">{{
                f.user
              }}</span>
              <span class="caption grey--text ml-2">
                in
                {{
                  f.channel_type === 'direct' || f.channel_type === 'group_dm'
                    ? ''
                    : '#'
                }}{{ f.channel_display_name }} · {{ f.collection }} ·
                {{ formatTime(f.date_generated) }}
              </span>
              <span
                v-if="f.date_generated !== f.last_modified"
                class="caption grey--text ml-1"
                >(edited)</span
              >
            </div>
            <!-- no whitespace inside: pre-wrap would render template indent as spaces -->
            <!-- prettier-ignore -->
            <div style="font-size: 0.85em; white-space: pre-wrap;" class="pa-0 ma-0">{{ resolveContent(f).length > 300 ? resolveContent(f).slice(0, 300) + '…' : resolveContent(f) }} </div>
            <div v-if="f.attachments && f.attachments.length" class="my-1">
              <img
                v-for="att in f.attachments"
                :key="att.id"
                :src="att.url"
                :alt="att.filename"
                class="msg-attachment-img"
                @click.stop="openImageDialog(att.url)"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="pa-2 pb-4 title">
        <span v-if="!feedLoading && feed.length > 0">
          <v-pagination
            v-model="pageNumber"
            :length="Math.ceil(feed.length / postsPerPage)"
          >
          </v-pagination>
        </span>
      </div>
    </v-sheet>
    <!-- Image lightbox dialog -->
    <v-dialog
      v-model="imageDialogOpen"
      content-class="image-lightbox-dialog"
      @click:outside="imageDialogOpen = false"
    >
      <img
        v-if="imageDialogSrc"
        :src="imageDialogSrc"
        style="max-width: 90vw; max-height: 90vh; display: block; cursor: pointer;"
        @click="imageDialogOpen = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { state } from '@/store/modules/auth/types';
import {
  actions as collaborationActions,
  getters as collaborationGetters,
} from '@/store/modules/collaboration/types';
import { format, formatDistance } from 'date-fns';
import resolveTagsToText from '@/utils/resolveTags';

export default {
  data() {
    return {
      features: {},
      feedLoading: true,
      sortedGroups: [],
      postsPerPage: 10,
      pageNumber: 1,
      imageDialogSrc: null,
      imageDialogOpen: false,
    };
  },
  computed: {
    ...mapState('auth', {
      user: state.USER,
    }),
    feed() {
      return (
        this.$store.getters['collaboration/' + collaborationGetters.FEED] || []
      );
    },
    channelsByCollection() {
      return (
        this.$store.getters[
          'collaboration/' + collaborationGetters.CHANNELS_BY_COLLECTION
        ] || {}
      );
    },
    usersById() {
      return (
        this.$store.getters[
          'collaboration/' + collaborationGetters.USERS_BY_ID
        ] || {}
      );
    },
  },
  watch: {
    user(u) {
      if (u != null) {
        this.updateSortedGroups();
      }
    },
  },
  created() {
    axios.get('/api/features_enabled').then(({ data }) => {
      this.features = data;
    });
  },
  async mounted() {
    this.feedLoading = true;
    await this.$store.dispatch(
      'collaboration/' + collaborationActions.FETCH_FEED
    );
    this.feedLoading = false;
    this.updateSortedGroups();
  },
  methods: {
    formatDateDistance(d) {
      return formatDistance(new Date(d), new Date(), { addSuffix: true });
    },
    formatTime(d) {
      if (!d) return '';
      return format(new Date(d), 'h:mm a');
    },
    resolveContent(msg) {
      const channels = this.channelsByCollection[msg.collection_id] || [];
      return resolveTagsToText(msg.content, channels, this.usersById);
    },
    userInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?';
    },
    getCurrentFeedPage() {
      let first = (this.pageNumber - 1) * this.postsPerPage;
      let last = first + this.postsPerPage;
      if (last > this.feed.length) {
        last = this.feed.length;
      }
      return this.feed.slice(first, last);
    },
    isNewDay(msg, index) {
      if (index === 0) return true;
      let first = (this.pageNumber - 1) * this.postsPerPage;
      const prev = this.feed[first + index - 1];
      const p = new Date(prev.date_generated);
      const c = new Date(msg.date_generated);
      return (
        p.getFullYear() !== c.getFullYear() ||
        p.getMonth() !== c.getMonth() ||
        p.getDate() !== c.getDate()
      );
    },
    formatDayLabel(d) {
      const date = new Date(d);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const msgDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      if (msgDay.getTime() === today.getTime()) return 'Today';
      if (msgDay.getTime() === yesterday.getTime()) return 'Yesterday';
      return format(date, 'MMMM d, yyyy');
    },
    updateSortedGroups() {
      if (this.user == null) {
        this.sortedGroups = [];
      } else {
        let groups = [...this.user.groups];
        groups = groups.sort((x, y) => x.name.localeCompare(y.name));
        this.sortedGroups = groups;
      }
    },
    openImageDialog(url) {
      this.imageDialogSrc = url;
      this.imageDialogOpen = true;
    },
  },
};
</script>

<style scoped>
/* Day separator banner */
.day-banner {
  display: flex;
  align-items: center;
  margin: 8px 12px 4px;
}
.day-banner::before,
.day-banner::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}
.day-banner-label {
  font-size: 0.65rem;
  color: #757575;
  padding: 2px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin: 0 8px;
  white-space: nowrap;
}

.message-row {
  position: relative;
  padding: 2px 12px 2px 40px;
  border-radius: 4px;
}
.message-row:hover {
  background: rgba(0, 0, 0, 0.04);
}
.msg-avatar {
  position: absolute;
  left: 12px;
  top: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  font-weight: 600;
}
.msg-avatar-lg {
  width: 20px;
  height: 20px;
  font-size: 0.6rem;
}
.msg-body {
  min-width: 0;
  position: relative;
}
.msg-meta {
  line-height: 1;
  align-items: baseline !important;
}
/* Message attachment images */
.msg-attachment-img {
  max-width: 200px;
  max-height: 180px;
  border-radius: 4px;
  cursor: pointer;
  display: block;
}
.msg-attachment-img--fs {
  max-width: 400px;
  max-height: 300px;
}
</style>
